import { htmlDiff } from '@benedicte/html-diff';
import type { Theme } from '@emotion/react';

const ZERO_WIDTH_REGEX = /[\u200B-\u200D\uFEFF]/g;

const normalizeText = (value: string): string =>
    value
        .normalize('NFC')
        .replace(/\u00A0/g, ' ')
        .replace(ZERO_WIDTH_REGEX, '')
        .replace(/\s+/g, ' ');

const basicSanitize = (html: string): string =>
    html
        .normalize('NFC')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00A0/g, ' ')
        .replace(ZERO_WIDTH_REGEX, '')
        .replace(/<\s*br\s*\/?\s*>/gi, '<br/>')
        .replace(/<\s*\/?\s*strong\s*>/gi, (match) => (match.startsWith('</') ? '</b>' : '<b>'))
        .replace(/\s+/g, ' ')
        .replace(/>\s+</g, '><')
        .trim();

const sanitizeHtml = (html: string): string => {
    if (html.length === 0) return '';

    if (typeof DOMParser === 'undefined') {
        return basicSanitize(html);
    }

    const normalizedInput = html
        .normalize('NFC')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00A0/g, ' ')
        .replace(ZERO_WIDTH_REGEX, '');

    const parser = new DOMParser();
    const doc = parser.parseFromString(normalizedInput, 'text/html');

    const normalizeNode = (node: Node): void => {
        if (node.nodeType === Node.TEXT_NODE) {
            node.textContent = normalizeText(node.textContent ?? '');

            return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
            (node as ChildNode).remove();

            return;
        }

        const element = node as HTMLElement;
        if (element.tagName.toLowerCase() === 'strong') {
            const replacement = doc.createElement('b');
            while (element.firstChild !== null) {
                replacement.appendChild(element.firstChild);
            }
            element.replaceWith(replacement);
            normalizeNode(replacement);

            return;
        }

        Array.from(element.childNodes).forEach(normalizeNode);
    };

    Array.from(doc.body.childNodes).forEach(normalizeNode);

    return doc.body.innerHTML
        .replace(/<\s*br\s*\/?\s*>/gi, '<br/>')
        .replace(/>\s+</g, '><')
        .trim();
};

const applyDiffStyling = ({
    diffHtml,
    theme,
    type,
}: {
    readonly diffHtml: string;
    readonly theme: Theme;
    readonly type: 'added' | 'removed';
}): string => {
    const isAdded = type === 'added';
    const style = isAdded
        ? `color: ${theme.colors.success[700]}; background-color: ${theme.colors.success[50]}`
        : `color: ${theme.colors.error[500]}; background-color: ${theme.colors.error[100]}`;
    const keepTag = isAdded ? 'ins' : 'del';
    const dropTag = isAdded ? 'del' : 'ins';

    return diffHtml
        .replace(new RegExp(`<${dropTag}[^>]*>[\\s\\S]*?<\\/${dropTag}>`, 'gi'), '')
        .replace(new RegExp(`<${keepTag}[^>]*>`, 'gi'), `<span style="${style}">`)
        .replace(new RegExp(`</${keepTag}>`, 'gi'), '</span>');
};

export const htmlDiffer = ({
    compareAgainst,
    current,
    theme,
    type,
}: {
    readonly compareAgainst: string;
    readonly current: string;
    readonly theme: Theme;
    readonly type: 'added' | 'removed';
}): string => {
    const sanitizedCompareAgainst = sanitizeHtml(compareAgainst);
    const sanitizedCurrent = sanitizeHtml(current);
    const diffHtml = htmlDiff(sanitizedCompareAgainst, sanitizedCurrent);
    const finalHtmlResult = applyDiffStyling({ diffHtml, theme, type });

    return `<div style="white-space: pre-wrap;">${finalHtmlResult}</div>`;
};
